import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SEO } from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { BlogContent } from "@/components/blog/BlogContent";
import { RelatedPosts } from "@/components/blog/RelatedPosts";
import { Calendar, Tag, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import CallToAction from "@/components/CallToAction";
import LazyImage from "@/components/ui/LazyImage";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { faqsForCategory, servicePathForCategory } from "@/data/faqs";

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: post, isLoading } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("is_published", true)
        .single();

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <p className="text-muted-foreground">Loading article...</p>
        </main>
        <Footer />
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex flex-col items-center justify-center">
          <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The article you're looking for doesn't exist.
          </p>
          <Link to="/blog">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  const publishedDate = new Date(post.published_date);
  const updatedDate = new Date(post.updated_at);
  const formattedDate = publishedDate.toLocaleDateString("en-US", dateFormatOptions);
  const formattedUpdatedDate = updatedDate.toLocaleDateString("en-US", dateFormatOptions);
  const wasUpdated = updatedDate.toDateString() !== publishedDate.toDateString();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.meta_description,
    image: post.featured_image,
    datePublished: post.published_date,
    dateModified: post.updated_at,
    author: {
      "@type": "Organization",
      name: post.author,
    },
    publisher: {
      "@type": "Organization",
      name: "Ventura Power Washing",
      logo: {
        "@type": "ImageObject",
        url: "https://www.venturapowerwashing.com/og-image.jpg",
      },
    },
  };

  const faqs = faqsForCategory(post.category);
  const relatedService = servicePathForCategory(post.category);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEO
        title={`${post.title} | Ventura Power Washing Blog`}
        description={post.meta_description}
        keywords={post.meta_keywords || undefined}
        image={post.featured_image || undefined}
        path={`/blog/${post.slug.trim()}`}
      >
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </SEO>

      <Navbar />

      <article className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-4 py-24">
          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Blog", href: "/blog" },
              { label: post.title, href: `/blog/${post.slug}` },
            ]}
          />

          <div className="max-w-4xl mx-auto">
            <Link to="/blog">
              <Button variant="ghost" className="mb-6">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Button>
            </Link>

            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Tag className="w-3 h-3" />
                  {post.category}
                </Badge>
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  Published {formattedDate}
                </span>
                {wasUpdated && (
                  <span className="text-muted-foreground">
                    · Last updated {formattedUpdatedDate}
                  </span>
                )}
              </div>

              <h1 className="text-5xl font-bold mb-4 leading-tight">
                {post.title}
              </h1>

              <p className="text-xl text-muted-foreground">{post.excerpt}</p>
            </header>

            {/* Featured Image */}
            {post.featured_image && (
              <div className="mb-8 rounded-lg overflow-hidden">
                <LazyImage
                  src={post.featured_image}
                  alt={`${post.title} - Professional ${post.category} advice from Ventura Power Washing`}
                  className="w-full"
                />
              </div>
            )}

            {/* Article Content */}
            <div className="mb-12">
              <BlogContent content={post.content} />
            </div>

            {/* Author Info */}
            <div className="border-t border-b py-6 mb-12">
              <p className="text-sm text-muted-foreground">
                Written by <span className="font-semibold">{post.author}</span>
              </p>
            </div>

            {/* Related Service CTA */}
            {relatedService && (
              <div className="mb-12 bg-primary/10 rounded-lg p-6 text-center">
                <p className="text-lg text-gray-700 mb-3">
                  Ready to put this into action?
                </p>
                <Link
                  to={relatedService.path}
                  className="text-primary font-semibold text-lg hover:underline"
                >
                  See our {relatedService.name} services →
                </Link>
              </div>
            )}

            {/* FAQ Section */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left text-lg font-medium">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* Related Posts */}
          <div className="max-w-6xl mx-auto">
            <RelatedPosts currentPostId={post.id} category={post.category} />
          </div>
        </div>

        {/* CTA Section */}
        <CallToAction />
      </article>

      <Footer />
    </>
  );
};

export default BlogPostPage;
